/**
 * « Mon QR code » (profil, migration 0334) : montre ce QR à un camarade, un
 * prof ou un parent — en le scannant il est relié à toi tout de suite. On peut
 * aussi saisir le code de quelqu'un, partager son lien, ou changer de code
 * (l'ancien QR ne marche plus).
 */

import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Check, Copy, RefreshCw, Share2 } from 'lucide-react';
import { Button, Input } from '@jeraisonne/ui';
import { QrCodeImage } from '@/components/qr-code';
import { copyText } from '@/lib/clipboard';
import { shareContent } from '@/lib/native-share';
import {
  connectUrl,
  formatConnectCode,
  normalizeConnectCode,
  useMyConnectCode,
  useRenewConnectCode,
} from '@/lib/connect-code';

export function MyConnectQr({ userId }: { userId: string }) {
  const navigate = useNavigate();
  const code = useMyConnectCode(userId);
  const renew = useRenewConnectCode(userId);
  const [copied, setCopied] = useState(false);
  const [other, setOther] = useState('');
  const [otherError, setOtherError] = useState<string | null>(null);
  const url = code.data ? connectUrl(code.data) : '';

  return (
    <div className="space-y-4">
      <p className="text-ink-600 text-sm">
        Montre ce QR code à un camarade, ton prof ou ton parent (ou ton enfant, ton élève) : en le
        scannant, il est relié à toi tout de suite.
      </p>
      <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-start">
        <div className="border-ink-100 rounded-2xl border bg-white p-2">
          {code.data ? (
            <QrCodeImage value={url} size={168} />
          ) : (
            <div className="bg-ink-100 h-[168px] w-[168px] animate-pulse rounded" />
          )}
        </div>
        <div className="w-full min-w-0 flex-1 space-y-2">
          {code.data && (
            <p className="text-ink-900 text-center font-mono text-2xl font-bold tracking-widest sm:text-left">
              {formatConnectCode(code.data)}
            </p>
          )}
          <div className="flex flex-col gap-2">
            <Button
              type="button"
              variant="secondary"
              fullWidth
              disabled={!code.data}
              onClick={() =>
                void shareContent({
                  title: 'Relions nos comptes sur JeRaisonne',
                  text: 'Ouvre ce lien pour te relier à moi sur JeRaisonne :',
                  url,
                })
              }
            >
              <Share2 className="h-4 w-4" aria-hidden /> Partager mon lien
            </Button>
            <Button
              type="button"
              variant="ghost"
              fullWidth
              disabled={!code.data}
              onClick={() =>
                void copyText(url).then((ok) => {
                  setCopied(ok);
                  if (ok) setTimeout(() => setCopied(false), 2000);
                })
              }
            >
              {copied ? (
                <Check className="h-4 w-4" aria-hidden />
              ) : (
                <Copy className="h-4 w-4" aria-hidden />
              )}
              {copied ? 'Lien copié' : 'Copier le lien'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              fullWidth
              loading={renew.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    'Changer de code ? Ton QR actuel ne marchera plus (utile s’il a été partagé par erreur).',
                  )
                ) {
                  renew.mutate();
                }
              }}
            >
              <RefreshCw className="h-4 w-4" aria-hidden /> Nouveau code
            </Button>
          </div>
        </div>
      </div>

      <form
        className="border-ink-100 space-y-2 border-t pt-4"
        onSubmit={(e) => {
          e.preventDefault();
          const c = normalizeConnectCode(other);
          if (!c) {
            setOtherError('Code invalide : 8 caractères (ou colle le lien).');
            return;
          }
          setOtherError(null);
          void navigate({ to: '/connexion/$code', params: { code: c } });
        }}
      >
        <label htmlFor="connect-other" className="text-ink-900 block text-sm font-semibold">
          Tu as le code de quelqu’un ?
        </label>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Input
            id="connect-other"
            value={other}
            onChange={(e) => setOther(e.target.value)}
            placeholder="ABCD-2345"
            autoCapitalize="characters"
          />
          <Button type="submit" className="sm:w-auto" fullWidth>
            Me relier
          </Button>
        </div>
        {otherError && (
          <p role="alert" className="text-error-600 text-sm">
            {otherError}
          </p>
        )}
      </form>
    </div>
  );
}
